<template>
  <div>
    <es-loader
      v-if="loading"
      style="margin: 4.5rem"
    />
    <es-exception
      v-else-if="loadingError"
      exceptionMessage="There was an issue. Please try again or contact your administrator."
      margin="1rem"
      style="padding: 5rem"
    />
    <es-exception
      v-else-if="locationNotFound"
      exceptionMessage="Location not found."
      margin="1rem"
      style="padding: 5rem"
    />
    <div v-else>
      <pid-retired-id-panel
        v-if="isRetired"
        :person="person"
      />
      <div
        class="document"
        :class="{ 'margin--top': isRetired }"
      >
        <div
          class="header"
          style="display: flex"
        >
          <EsHeader>
            <template #header>
              <div class="header-title">
                Location Information Details
                ({{ cachedInfo.personTypeName }})
              </div>
            </template>
          </EsHeader>
          <div
            class="icon-right"
            v-if="!editCurrentPanel
              && (!this.isRetired)
              && userCanEditThisLoc
              && personTypeAccess
              && this.activeModule.name === 'personId'"
          >
            <a
              role="button"
              tabindex="0"
              @keyup.enter="editPersonLocation()"
              @click="editPersonLocation()"
            >
              <font-awesome-icon
                :icon="['far', 'edit']"
                size="lg"
                title="Edit"
              />
            </a>
          </div>
          <div class="icon-right">
            <router-link
              v-if="this.activeModule.name === 'personId' && !editCurrentPanel"
              tabindex="0"
              title="View details"
              :to="{
                name: 'PidMasterRecord',
                query: {
                  retired: isRetired,
                },
              }"
            >
              <font-awesome-icon
                :icon="['far', 'angle-double-left']"
                size="2x"
                title="View Details"
              />
            </router-link>
            <router-link
              v-if="this.activeModule.name === 'directMatch'"
              tabindex="0"
              title="View details"
              :to="{
                name: 'DmMasterRecord',
                query: {
                  retired: isRetired,
                },
              }"
            >
              <font-awesome-icon
                :icon="['far', 'angle-double-left']"
                size="2x"
                title="View Details"
              />
            </router-link>
          </div>
        </div>
        <div style="padding: 1rem 0">
          <div class="columns">
            <div
              class="column district-name"
              style="border-right: 1px solid #dee2e6 !important;"
            >
              {{ cachedInfo.districtName || cachedInfo.district.location.name }}
              <span
                class="district-code"
                style="padding-left: 0.25rem"
              >
                ({{ cachedInfo.districtCode }})
              </span>
              <div class="school-name">
                {{ cachedInfo.schoolName || cachedInfo.school.location.name }}
                <span
                  class="school-code"
                  style="padding-left: 0.25rem;
                    font-style: italic"
                >
                  ({{ cachedInfo.schoolCode }})
                </span>
              </div>
            </div>
            <div
              class="column"
              style="border-right: 1px solid #dee2e6 !important"
            >
              <div style="font-size: 16px; font-weight: 700">
                {{ cachedInfo.lastUpdateDate | formatDateMMDDYYYY }}
              </div>
              <div class="school-code">
                Last Updated
              </div>
            </div>
            <div class="column">
              <div style="font-size: 16px; font-weight: 700">
                {{ cachedInfo.personTypeName }}
              </div>
              <div class="school-code">
                Person Type
              </div>
            </div>
          </div>
        </div>
        <es-form style="margin-right: 0rem;">
          <template #es-form>
            <div
              class="content"
              style="padding: 1rem 0"
            >
              <div class="columns">
                <div class="column main-column">
                  <div
                    v-if="fieldEnabled('SCHOOL_YEAR')"
                    class="row"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('SCHOOL_YEAR') }}
                          <span
                            v-if="!fieldRequired('SCHOOL_YEAR')
                              && editCurrentPanel"
                            class="label-optional has-text-grey-light"
                          >
                            &nbsp;(optional)
                          </span>
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            <es-select
                              v-if="editCurrentPanel"
                              keyValue="id"
                              valueTpl="${year}"
                              v-on:update:values="
                                personLocation.schoolYear = $event;
                                validateSelectField('schoolYear')"
                              :errorMsg="msgSchoolYear"
                              :hasError="hasErrorSchoolYear"
                              :preSelected="preSelectedSchoolYear"
                              :setValues="schoolYears"
                            />
                            <input
                              v-else
                              class="input"
                              type="text"
                              :class="{ 'is-static': !editCurrentPanel }"
                              :readonly="!editCurrentPanel"
                              :value="personLocation.schoolYear"
                              :aria-label="fieldLabel('SCHOOL_YEAR')"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="fieldEnabled('DISTRICT_CODE')"
                    class="row"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('DISTRICT_CODE') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            <es-select
                              disabled
                              v-if="editCurrentPanel"
                              api="districts"
                              :preSelected="preSelectedDistrict"
                              :valueTplFn="(result) => `${result.location.name} (${result.code})`"
                            />
                            <div
                              v-else
                              class="input is-static is-readonly"
                              v-html="districtNameAndCode"
                            >
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="fieldEnabled('SCHOOL_CODE')"
                    class="row"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('SCHOOL_CODE') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            <es-select
                              v-if="editCurrentPanel && !concurrentLocAssign"
                              api="schools"
                              keyValue="code"
                              ref="selSchools"
                              searchMinLength="2"
                              :apiQueryParams="getDistrictParams"
                              :errorMsg="msgSchool"
                              :hasError="hasErrorSchool"
                              :preSelected="schoolCodesPromises"
                              :search="true"
                              :subValueTplFn="(result) =>
                                `${result.districtLocation.name} (<i>${result.districtCode}</i>)`"
                              :valueTplFn="(result) => `${result.location.name} (${result.code})`"
                              @update:values="personLocation.schoolCode = $event;
                                validateSelectField('schoolCode')"
                            />
                            <div
                              v-else
                              class="input is-static is-readonly"
                              v-html="schoolNameAndCode"
                            >
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="fieldEnabled('RES_DISTRICT_CODE')"
                    class="row"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('RES_DISTRICT_CODE') }}
                          <span
                            v-if="!fieldRequired('RES_DISTRICT_CODE')
                              && editCurrentPanel"
                            class="label-optional has-text-grey-light"
                          >
                            &nbsp;(optional)
                          </span>
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            <es-select
                              v-if="editCurrentPanel"
                              api="districts"
                              keyValue="code"
                              searchMinLength="2"
                              v-on:update:values="
                                personLocation.resDistrictCode = $event;
                                validateSelectField('resDistrictCode')"
                              :apiQueryParams="getAll"
                              :errorMsg="msgResDistrict"
                              :hasError="hasErrorResDistrict"
                              :preSelected="preSelectedResDistrict"
                              :search="true"
                              :valueTplFn="(result) => `${result.location.name} (${result.code})`"
                            />
                            <div
                              v-else
                              class="input is-static is-readonly"
                              v-html="resDistrictNameAndCode"
                            >
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="fieldEnabled('LOCAL_STUDENT_ID')"
                    class="row"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('LOCAL_STUDENT_ID') }}
                          <span
                            v-if="!fieldRequired('LOCAL_STUDENT_ID')
                              && editCurrentPanel"
                            class="label-optional has-text-grey-light"
                          >
                            &nbsp;(optional)
                          </span>
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal" :title="personLocation.localId">
                            <input
                              class="input"
                              type="text"
                              v-model="personLocation.localId"
                              :class="{
                                'is-static': !editCurrentPanel,
                                'is-danger': hasErrorLocalId,
                              }"
                              :readonly="!editCurrentPanel"
                              @keyup.prevent="validateInputField('localId')"
                              @paste="onPasteValidation('localId')"
                              :aria-label="fieldLabel('LOCAL_STUDENT_ID')"
                            />
                          </div>
                          <span
                            v-if="hasErrorLocalId"
                            class="help is-danger"
                          >
                            {{ msgLocalId }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="fieldEnabled('SOURCE_CODE')"
                    class="row"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('SOURCE_CODE') }}
                          <span
                            v-if="!fieldRequired('SOURCE_CODE')
                              && editCurrentPanel"
                            class="label-optional has-text-grey-light"
                          >
                            &nbsp;(optional)
                          </span>
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal" :title="mapSource(cachedInfo.sourceCode)">
                            <es-select
                              v-if="editCurrentPanel"
                              keyValue="code"
                              valueTpl="${description}"
                              v-on:update:values="
                                personLocation.sourceCode = $event;
                                validateSelectField('sourceCode')"
                              :clearOut="true"
                              :errorMsg="msgSource"
                              :hasError="hasErrorSource"
                              :preSelected="preSelectedSource"
                              :setValues="activeSources"
                            />
                            <input
                              v-else
                              class="input"
                              type="text"
                              :class="{ 'is-static': !editCurrentPanel }"
                              :readonly="!editCurrentPanel"
                              :value="mapSource(cachedInfo.sourceCode)"
                              :aria-label="fieldLabel('SOURCE_CODE')"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="fieldEnabled('ALT_STUDENT_ID')"
                    class="row"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('ALT_STUDENT_ID') }}
                          <span
                            v-if="!fieldRequired('ALT_STUDENT_ID')
                              && editCurrentPanel"
                            class="label-optional has-text-grey-light"
                          >
                            &nbsp;(optional)
                          </span>
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal" :title="personLocation.altPersonId">
                            <input
                              class="input"
                              type="text"
                              v-model="personLocation.altPersonId"
                              :class="{
                                'is-static': !editCurrentPanel,
                                'is-danger': hasErrorAltPersonId,
                              }"
                              :readonly="!editCurrentPanel"
                              @keyup.prevent="validateInputField('altPersonId');
                                validateAltIDAndAltSourceLinkage()"
                              @paste="handlePaste"
                              :aria-label="fieldLabel('ALT_STUDENT_ID')"
                            />
                          </div>
                          <div
                            v-if="hasErrorAltPersonId"
                            class="help is-danger"
                          >
                            {{ msgAltPersonId }}
                        </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="fieldEnabled('ALT_SOURCE_SIS_CODE')"
                    class="row"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('ALT_SOURCE_SIS_CODE') }}
                          <span
                            v-if="!fieldRequired('ALT_SOURCE_SIS_CODE')
                              && editCurrentPanel"
                            class="label-optional has-text-grey-light"
                          >
                            &nbsp;(optional)
                          </span>
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal" :title="mapSource(cachedInfo.altSourceCode)">
                            <es-select
                              v-if="editCurrentPanel"
                              keyValue="code"
                              valueTpl="${description}"
                              v-on:update:values="
                                personLocation.altSourceCode = $event;
                                validateSelectField('altSourceCode');
                                validateAltIDAndAltSourceLinkage()"
                              :clearOut="true"
                              :errorMsg="msgAltSource"
                              :hasError="hasErrorAltSource"
                              :setValues="activeSources"
                              :preSelected="preSelectedAltSource"
                            />
                            <input
                              v-else class="input"
                              type="text"
                              :class="{ 'is-static': !editCurrentPanel }"
                              :readonly="!editCurrentPanel"
                              :value="mapSource(cachedInfo.altSourceCode)"
                              :aria-label="fieldLabel('ALT_SOURCE_SIS_CODE')"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="fieldEnabled('ACTIVE')"
                    class="row"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('ACTIVE') }}
                          <span
                            v-if="!fieldRequired('ACTIVE')
                              && editCurrentPanel"
                            class="label-optional has-text-grey-light"
                          >
                            &nbsp;(optional)
                          </span>
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            <es-select
                              v-if="editCurrentPanel"
                              keyValue="code"
                              valueTpl="${description}"
                              v-on:update:values="
                                personLocation.active = $event;
                                validateSelectField('active')"
                              :clearOut="true"
                              :errorMsg="msgActive"
                              :hasError="hasErrorActive"
                              :preSelected="preSelectedActiveStatus"
                              :setValues="activeStatusOptions"
                            />
                            <input
                              v-else
                              class="input"
                              type="text"
                              :class="{ 'is-static': !editCurrentPanel }"
                              :readonly="!editCurrentPanel"
                              :value="activeStatus"
                              :aria-label="fieldLabel('ACTIVE')"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="fieldEnabled('SOURCE_REFRESH_DATE')"
                    class="row"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('SOURCE_REFRESH_DATE') }}
                          <span
                            v-if="!fieldRequired('SOURCE_REFRESH_DATE')
                              && editCurrentPanel"
                            class="label-optional has-text-grey-light"
                          >
                            &nbsp;(optional)
                          </span>
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            <input
                              v-if="editCurrentPanel"
                              class="input calendar"
                              type="date"
                              v-model="editableSourceRefreshDate"
                              :class="{ 'is-danger': hasErrorSourceRefreshDate }"
                              @change.prevent="validateDateField('sourceRefreshDate')"
                              :aria-label="fieldLabel('SOURCE_REFRESH_DATE')"
                            />
                            <input
                              v-else class="input"
                              type="text"
                              :class="{ 'is-static': !editCurrentPanel }"
                              :readonly="!editCurrentPanel"
                              :value="cachedInfo.sourceRefreshDate | formatDatePreserveZone"
                              :aria-label="fieldLabel('SOURCE_REFRESH_DATE')"
                            />
                          </div>
                          <span
                            v-if="hasErrorSourceRefreshDate"
                            class="help is-danger"
                          >
                            {{ msgSourceRefreshDate }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="fieldEnabled('LOCATION_COMMENT')"
                    class="row"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('LOCATION_COMMENT') }}
                          <span
                            v-if="!fieldRequired('LOCATION_COMMENT')
                              && editCurrentPanel"
                            class="label-optional has-text-grey-light"
                          >
                            &nbsp;(optional)
                          </span>
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            <div class="textarea-wrapper">
                              <textarea
                                maxlength="100"
                                rows="3"
                                style="width: 100%;
                                  resize: none;
                                  padding-left: 10px;
                                  overflow: auto;"
                                v-model="personLocation.locationComment"
                                :class="{
                                  'disabled-textarea': !editCurrentPanel,
                                  'border-red': hasErrorLocationComment,
                                }"
                                :readonly="!editCurrentPanel"
                                @keyup.prevent="validateInputField('locationComment')"
                                @paste="onPasteValidation('locationComment')"
                                :aria-label="fieldLabel('LOCATION_COMMENT')"
                              ></textarea>
                            </div>
                          </div>
                          <span
                            v-if="hasErrorLocationComment"
                            class="help is-danger"
                          >
                            {{ msgLocationComment }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="column main-column">
                  <div
                    v-if="fieldEnabled('GRADE_CODE')
                      && cachedInfo.personTypeCategory === 'STUDENT'"
                    class="row"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('GRADE_CODE') }}
                          <span
                            v-if="!fieldRequired('GRADE_CODE')
                              && editCurrentPanel"
                            class="label-optional has-text-grey-light"
                          >
                            &nbsp;(optional)
                          </span>
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal" :title="mapGrades(cachedInfo.gradeCode)">
                            <es-select
                              v-if="editCurrentPanel"
                              keyValue="code"
                              valueTpl="${description}"
                              v-on:update:values="
                                personLocation.gradeCode = $event;
                                validateSelectField('gradeCode')"
                              :clearOut="true"
                              :errorMsg="msgGrade"
                              :hasError="hasErrorGrade"
                              :preSelected="preSelectedGrade"
                              :setValues="activeGrades"
                            />
                            <input
                              v-else class="input"
                              type="text"
                              :class="{
                                'is-static': !editCurrentPanel,
                              }"
                              :readonly="!editCurrentPanel"
                              :value="mapGrades(cachedInfo.gradeCode)"
                              :aria-label="fieldLabel('GRADE_CODE')"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="fieldEnabled('HIRE_DATE')
                      && cachedInfo.personTypeCategory === 'EMPLOYEE'"
                    class="row"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('HIRE_DATE') }}
                          <span
                            v-if="!fieldRequired('HIRE_DATE')
                              && editCurrentPanel"
                            class="label-optional has-text-grey-light"
                          >
                            &nbsp;(optional)
                          </span>
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            <input
                              v-if="editCurrentPanel"
                              class="input calendar"
                              type="date"
                              v-model="personLocation.hireDate"
                              :class="{ 'is-danger': hasErrorHireDate }"
                              @change.prevent="validateDateField('hireDate')"
                              :aria-label="fieldLabel('HIRE_DATE')"
                            />
                            <input
                              v-else
                              class="input"
                              type="text"
                              :class="{ 'is-static': !editCurrentPanel }"
                              :readonly="!editCurrentPanel"
                              :value="cachedInfo.hireDate | formatDateMMDDYYYY"
                              :aria-label="fieldLabel('HIRE_DATE')"
                            />
                          </div>
                          <span
                            v-if="hasErrorHireDate"
                            class="help is-danger"
                          >
                            {{ msgHireDate }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="fieldEnabled('SALARY')
                      && cachedInfo.personTypeCategory === 'EMPLOYEE'"
                    class="row"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('SALARY') }}
                          <span
                            v-if="!fieldRequired('SALARY')
                              && editCurrentPanel"
                            class="label-optional has-text-grey-light"
                          >
                            &nbsp;(optional)
                          </span>
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal" :title="personLocation.salary">
                            <input
                              class="input"
                              :class="{
                                'is-static': !editCurrentPanel,
                                'is-danger': hasErrorSalary,
                              }"
                              type="text"
                              v-model="personLocation.salary"
                              :readonly="!editCurrentPanel"
                              @blur="annSalaryFocusOut"
                              @focus="annSalaryFocusIn"
                              @keyup.prevent="validateSalary()"
                              @keypress="isFloatNumbers($event)"
                              :aria-label="fieldLabel('SALARY')"
                            />
                          </div>
                          <span
                            v-if="hasErrorSalary"
                            class="help is-danger"
                          >
                            {{ msgSalary }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="fieldEnabled('JOB_CLASS_CODE')
                      && cachedInfo.personTypeCategory === 'EMPLOYEE'"
                    class="row"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('JOB_CLASS_CODE') }}
                          <span
                            v-if="!fieldRequired('JOB_CLASS_CODE')
                              && editCurrentPanel"
                            class="label-optional has-text-grey-light"
                          >
                            &nbsp;(optional)
                          </span>
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal" :title="mapJobClassCode(cachedInfo.jobClassCode)">
                            <es-select
                              v-if="editCurrentPanel"
                              keyValue="code"
                              valueTpl="${description}"
                              v-on:update:values="
                                personLocation.jobClassCode = $event;
                                validateSelectField('jobClassCode')"
                              :clearOut="true"
                              :errorMsg="msgJobClass"
                              :hasError="hasErrorJobClass"
                              :preSelected="preSelectedJobClassCode"
                              :setValues="activeTitles"
                            />
                            <input
                              v-else class="input"
                              type="text"
                              :class="{
                                'is-static': !editCurrentPanel,
                              }"
                              :readonly="!editCurrentPanel"
                              :value="mapJobClassCode(cachedInfo.jobClassCode)"
                              :aria-label="fieldLabel('JOB_CLASS_CODE')"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="fieldEnabled('JOB_ASSIGNMENT_1')
                      && cachedInfo.personTypeCategory === 'EMPLOYEE'"
                    class="row"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('JOB_ASSIGNMENT_1') }}
                          <span
                            v-if="!fieldRequired('JOB_ASSIGNMENT_1')
                              && editCurrentPanel"
                            class="label-optional has-text-grey-light"
                          >
                            &nbsp;(optional)
                          </span>
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal" :title="mapJobAssignments(cachedInfo.jobAssignment1)">
                            <es-select
                              v-if="editCurrentPanel"
                              keyValue="code"
                              valueTpl="${description}"
                              v-on:update:values="
                                personLocation.jobAssignment1 = $event;
                                validateSelectField('jobAssignment1')"
                              :clearOut="true"
                              :errorMsg="msgJobAssign1"
                              :hasError="hasErrorJobAssign1"
                              :preSelected="preSelectedJobAssignment1"
                              :setValues="activeJobAssignments"
                            />
                            <input
                              v-else
                              class="input"
                              type="text"
                              :class="{
                                'is-static': !editCurrentPanel,
                              }"
                              :readonly="!editCurrentPanel"
                              :value="mapJobAssignments(cachedInfo.jobAssignment1)"
                              :aria-label="fieldLabel('JOB_ASSIGNMENT_1')"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="fieldEnabled('JOB_ASSIGNMENT_2')
                      && cachedInfo.personTypeCategory === 'EMPLOYEE'"
                    class="row"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('JOB_ASSIGNMENT_2') }}
                          <span
                            v-if="!fieldRequired('JOB_ASSIGNMENT_2')
                              && editCurrentPanel"
                            class="label-optional has-text-grey-light"
                          >
                            &nbsp;(optional)
                          </span>
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal" :title="mapJobAssignments(cachedInfo.jobAssignment2)">
                            <es-select
                              v-if="editCurrentPanel"
                              keyValue="code"
                              valueTpl="${description}"
                              v-on:update:values="
                                personLocation.jobAssignment2 = $event;
                                validateSelectField('jobAssignment2')"
                              :clearOut="true"
                              :errorMsg="msgJobAssign2"
                              :hasError="hasErrorJobAssign2"
                              :preSelected="preSelectedJobAssignment2"
                              :setValues="activeJobAssignments"
                            />
                            <input
                              v-else
                              class="input"
                              type="text"
                              :class="{
                                'is-static': !editCurrentPanel,
                              }"
                              :readonly="!editCurrentPanel"
                              :value="mapJobAssignments(cachedInfo.jobAssignment2)"
                              :aria-label="fieldLabel('JOB_ASSIGNMENT_2')"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="fieldEnabled('JOB_ASSIGNMENT_3')
                      && cachedInfo.personTypeCategory === 'EMPLOYEE'"
                    class="row"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('JOB_ASSIGNMENT_3') }}
                          <span
                            v-if="!fieldRequired('JOB_ASSIGNMENT_3')
                              && editCurrentPanel"
                            class="label-optional has-text-grey-light"
                          >
                            &nbsp;(optional)
                          </span>
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal" :title="mapJobAssignments(cachedInfo.jobAssignment3)">
                            <es-select
                              v-if="editCurrentPanel"
                              keyValue="code"
                              valueTpl="${description}"
                              v-on:update:values="
                                personLocation.jobAssignment3 = $event;
                                validateSelectField('jobAssignment3')"
                              :clearOut="true"
                              :errorMsg="msgJobAssign3"
                              :hasError="hasErrorJobAssign3"
                              :preSelected="preSelectedJobAssignment3"
                              :setValues="activeJobAssignments"
                            />
                            <input
                              v-else
                              class="input"
                              type="text"
                              :class="{
                                'is-static': !editCurrentPanel,
                              }"
                              :readonly="!editCurrentPanel"
                              :value="mapJobAssignments(cachedInfo.jobAssignment3)"
                              :aria-label="fieldLabel('JOB_ASSIGNMENT_3')"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="fieldEnabled('ITINERANT_FLAG')
                      && cachedInfo.personTypeCategory === 'EMPLOYEE'"
                    class="row"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('ITINERANT_FLAG') }}
                          <span
                            v-if="!fieldRequired('ITINERANT_FLAG')
                              && editCurrentPanel"
                            class="label-optional has-text-grey-light"
                          >
                            &nbsp;(optional)
                          </span>
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            <es-select
                              v-if="editCurrentPanel"
                              keyValue="code"
                              valueTpl="${description}"
                              v-on:update:values="
                                personLocation.itinerantFlag = $event;
                                validateSelectField('itinerantFlag')"
                              :clearOut="true"
                              :errorMsg="msgItinerantFlag"
                              :hasError="hasErrorItinerantFlag"
                              :preSelected="preSelectedItinerantFlag"
                              :setValues="activeItinerantFlags"
                            />
                            <input
                              v-else
                              class="input"
                              type="text"
                              :class="{
                                'is-static': !editCurrentPanel,
                              }"
                              :readonly="!editCurrentPanel"
                              :value="cachedInfo.itinerantFlag
                                ? mapItinerantFlag(cachedInfo.itinerantFlag) : null"
                              :aria-label="fieldLabel('ITINERANT_FLAG')"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </es-form>
        <div
          v-if="editCurrentPanel"
          class="is-flex flex-justify--end flex-align--center action-buttons"
          style="margin-right: 5.15rem; padding-bottom: 2rem"
        >
          <button
            class="button button-link has-underline"
            style="margin-right: 1rem"
            @click.prevent="cancelUpdate()"
          >
            Cancel
          </button>
          <button
            class="button is-primary"
            @click.prevent="updateLocation()"
          >
            Update
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faAngleDoubleLeft,
  faEdit,
} from '@fortawesome/pro-regular-svg-icons';
import EsException from '@/components/EsException.vue';
import EsForm from '@/components/EsForm.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsSelect from '@/components/EsSelect.vue';
import PidRetiredIdPanel from '@/components/PidRetiredIdPanel.vue';
import DateUtils from '@/mixins/DateUtils';
import FieldLabels from '@/mixins/FieldLabels';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';
import SchoolYearsAllowed from '@/mixins/SchoolYearsAllowed';

library.add(
  faAngleDoubleLeft,
  faEdit,
);

export default {
  mixins: [
    DateUtils,
    FieldLabels,
    NotificationManager,
    PermissionManager,
    SchoolYearsAllowed,
  ],
  components: {
    EsException,
    EsForm,
    EsHeader,
    EsLoader,
    EsSelect,
    PidRetiredIdPanel,
  },
  props: {
    isRetired: {
      type: Boolean,
      default: false,
    },
    person: {
      type: Object,
      required: true,
    },
  },
  data() {
    const errorFields = [
      'SchoolYear', 'ResDistrict', 'School', 'Source', 'LocalId',
      'AltSource', 'AltPersonId', 'Active', 'SourceRefreshDate',
      'LocationComment', 'Grade', 'HireDate', 'Salary',
      'JobClass', 'JobAssign1', 'JobAssign2', 'JobAssign3', 'ItinerantFlag',
    ];

    const temp = {
      activeStatusOptions: [],
      allLocations: null,
      cachedInfo: {},
      editCurrentPanel: false,
      personLocation: {},
      loading: false,
      loadingError: false,
      locationNotFound: false,
      payLoad: {},
      resDistrictNameAndCode: null,
      salaryTemporary: null,
    };

    // Dynamically add `hasError` and `msg` to error fields
    errorFields.forEach((field) => {
      temp[`hasError${field}`] = false;
      temp[`msg${field}`] = null;
    });

    return temp;
  },
  created() {
    this.onLoad();
    this.setActiveStatusOptions();
  },
  mounted() {
    const storedNotification = sessionStorage.getItem('notificationsLocation');
    if (storedNotification) {
      const { type, message } = JSON.parse(storedNotification);
      this.showNotification(type, message);
      sessionStorage.removeItem('notificationsLocation');
    }
  },
  computed: {
    ...mapGetters({
      activeModule: 'appModules/getActiveModule',
    }),
    ...mapGetters('propertiesModule', [
      'properties',
    ]),
    ...mapGetters('lookups', [
      'activeGrades',
      'activeSources',
      'sources',
      'activeJobAssignments',
      'activeItinerantFlags',
      'activeTitles',
      'filteredSchoolYears',
      'pidSchoolYearsDesc',
    ]),
    ...mapGetters('fieldLabelsModule', [
      'getFieldLabelByPersonType',
      'getFieldLengthsByPersonType',
      'getFieldEnabledForPersonType',
      'getFieldRequiredForPersonType',
    ]),
    activeStatus() {
      if (this.cachedInfo.active === true) {
        return 'Active';
      }
      if (this.cachedInfo.active === false) {
        return 'Inactive';
      }
      return null;
    },
    editableSourceRefreshDate: {
      get() {
        // The calendar picker shows the date portion only, even when the stored
        // value carries a time component (e.g. from data migration or the API).
        // The getter never mutates the model, so an untouched record keeps its
        // original time on save.
        return this.toDateInputValue(this.personLocation.sourceRefreshDate);
      },
      set(value) {
        // Editing via the calendar picker stores a date-only value, which zeroes
        // out any time component previously retained on the record.
        this.personLocation.sourceRefreshDate = value || null;
      },
    },
    concurrentLocAssign() {
      const { userPersonTypes } = this.$store.state.user;
      const personType = userPersonTypes.find(
        type => type.personTypeCode === this.cachedInfo.personType,
      );
      return personType ? personType.personType.concurrentLocAssign : false;
    },
    districtNameAndCode() {
      return this.formatNameAndCode(
        this.cachedInfo.districtName, this.cachedInfo.districtCode,
      );
    },
    getAll() {
      return {
        allDistricts: true,
      };
    },
    getDistrictParams() {
      return {
        districtCode: [this.cachedInfo.districtCode],
      };
    },
    personTypeAccess() {
      return this.$store.state.user.userPersonTypes
        .some(item => item.personType.code === this.cachedInfo.personType);
    },
    preSelectedSchoolYear() {
      return [this.pidSchoolYearsDesc.find(e => e.year === this.cachedInfo.schoolYear)];
    },
    preSelectedDistrict() {
      if (this.cachedInfo.districtCode && this.cachedInfo.districtCode.length) {
        return [this.cachedInfo.districtCode];
      } return null;
    },
    preSelectedResDistrict() {
      if (this.cachedInfo.resDistrictCode && this.cachedInfo.resDistrictCode.length) {
        return [this.cachedInfo.resDistrictCode];
      } return null;
    },
    preSelectedGrade() {
      return this.activeGrades.filter(s => s.code === this.cachedInfo.gradeCode);
    },
    preSelectedAltSource() {
      return this.activeSources.filter(s => s.code === this.cachedInfo.altSourceCode);
    },
    preSelectedSource() {
      return this.activeSources.filter(s => s.code === this.cachedInfo.sourceCode);
    },
    preSelectedJobClassCode() {
      return this.activeTitles.filter(s => s.code === this.cachedInfo.jobClassCode);
    },
    preSelectedActiveStatus() {
      return this.activeStatusOptions.filter(s => s.code === this.cachedInfo.active);
    },
    preSelectedJobAssignment1() {
      return this.activeJobAssignments.filter(s => s.code === this.cachedInfo.jobAssignment1);
    },
    preSelectedJobAssignment2() {
      return this.activeJobAssignments.filter(s => s.code === this.cachedInfo.jobAssignment2);
    },
    preSelectedJobAssignment3() {
      return this.activeJobAssignments.filter(s => s.code === this.cachedInfo.jobAssignment3);
    },
    preSelectedItinerantFlag() {
      const { itinerantFlag } = this.cachedInfo;
      if (itinerantFlag && itinerantFlag !== '') {
        return this.activeItinerantFlags.filter(flag => flag.code === itinerantFlag);
      } return [];
    },
    schoolCodesPromises() {
      return this.fetchSchoolCodes();
    },
    schoolNameAndCode() {
      return this.formatNameAndCode(this.cachedInfo.schoolName, this.cachedInfo.schoolCode);
    },
    tempPayLoad() {
      const getFirstElementOrNull = (field) => {
        const fieldValue = this.personLocation[field];
        if (Array.isArray(fieldValue)) {
          if (fieldValue.length === 0) {
            return null;
          }
          return field === 'schoolYear' ? fieldValue[0].year : fieldValue[0].code;
        }
        return fieldValue !== undefined ? fieldValue : null;
      };

      const temp = {
        personId: this.personLocation.personId,
        personType: this.personLocation.personType,
        districtCode: this.personLocation.districtCode,
        schoolCode: getFirstElementOrNull('schoolCode'),
        schoolYear: getFirstElementOrNull('schoolYear'),
        resDistrictCode: getFirstElementOrNull('resDistrictCode'),
        localId: this.personLocation.localId || null,
        sourceCode: getFirstElementOrNull('sourceCode'),
        altPersonId: this.personLocation.altPersonId || null,
        altSourceCode: getFirstElementOrNull('altSourceCode'),
        active: getFirstElementOrNull('active'),
        sourceRefreshDate: this.personLocation.sourceRefreshDate || null,
        locationComment: this.personLocation.locationComment || null,
      };

      if (this.personLocation.personTypeCategory === 'STUDENT') {
        temp.gradeCode = getFirstElementOrNull('gradeCode');
      }

      if (this.personLocation.personTypeCategory === 'EMPLOYEE') {
        temp.hireDate = this.personLocation.hireDate || null;
        temp.salary = this.personLocation.salary || null;
        temp.jobClassCode = getFirstElementOrNull('jobClassCode');
        temp.jobAssignment1 = getFirstElementOrNull('jobAssignment1');
        temp.jobAssignment2 = getFirstElementOrNull('jobAssignment2');
        temp.jobAssignment3 = getFirstElementOrNull('jobAssignment3');
        temp.itinerantFlag = getFirstElementOrNull('itinerantFlag');
      }

      return temp;
    },
    userCanEditThisLoc() {
      const { locationPermissions } = this.$store.state.user;

      // If there are no location permissions, means it has all districts and all schools access
      if (!locationPermissions.length) return true;

      // check if user has edit permission for this district and school
      // we look for a match on district and profile edit permission
      // AND... either no schools (all school access for district) or a match on school
      const list = locationPermissions.filter(lp => (!lp.districts.length || lp.districts
        .find(d => d.code === this.person.districtCode) != null)
        && (!lp.schools.length || lp.schools.find(d => d.code === this.person.schoolCode) != null)
        && lp.permissions.find(p => p === 'PRSID:PRPRF:PROFILE:EDIT') != null);

      return list && list.length > 0;
    },
  },
  methods: {
    annSalaryFocusIn() {
      if (this.salaryTemporary !== null) {
        // Unformat  Salary value before user starts modifying it
        this.personLocation.salary = this.salaryTemporary.toString();
      }
    },
    annSalaryFocusOut() {
      if (this.personLocation.salary !== null) {
        // Recalculate the  Salary after ignoring the digits and "." in user input
                this.salaryTemporary = parseFloat(this.personLocation.salary.replace(/[^\d\.]/g, "")); // eslint-disable-line
      }
      if (Number.isNaN(this.salaryTemporary)) {
        // Ensure that value is not NaN.
        // If so, initialize it to 'this.personLocation.salary (NULL)'.
        // This happens if user provides a blank input
        this.salaryTemporary = this.personLocation.salary;
      }
      if (this.salaryTemporary !== null) {
        // Format display value based on calculated  Salary
        this.personLocation.salary = this.salaryTemporary.toFixed(2).replace(/(\d)(?=(\d{3})+(?:\.,\d+)?$)/g, '$1,');
      }
    },
    cancelUpdate() {
      this.resetData();
      this.resetErrorAndMsg();
      this.editCurrentPanel = false;
      this.hideNotification();
    },
    checkFieldValidations() {
      // Define arrays for field validation types
      const selectFields = ['schoolYear', 'schoolCode', 'sourceCode', 'resDistrictCode', 'altSourceCode', 'active'];
      const inputFields = ['localId', 'altPersonId', 'locationComment'];
      const dateFields = ['sourceRefreshDate'];
      const additionalStudentFields = this.cachedInfo.personTypeCategory === 'STUDENT' ? ['gradeCode'] : [];
      const additionalEmployeeFields = this.cachedInfo.personTypeCategory === 'EMPLOYEE'
        ? ['hireDate', 'jobClassCode', 'jobAssignment1', 'jobAssignment2', 'jobAssignment3', 'itinerantFlag']
        : [];

      // Validate fields
      selectFields.concat(additionalStudentFields, additionalEmployeeFields)
        .forEach(field => this.validateSelectField(field));
      inputFields.forEach(field => this.validateInputField(field));
      dateFields.forEach(field => this.validateDateField(field));

      if (this.cachedInfo.personTypeCategory === 'EMPLOYEE') {
        this.validateSalary();
      }

      // Check for errors
      const errorFields = [
        'hasErrorSchoolYear', 'hasErrorResDistrict', 'hasErrorSchool', 'hasErrorSource', 'hasErrorLocalId',
        'hasErrorAltPersonId', 'hasErrorAltSource', 'hasErrorActive', 'hasErrorSourceRefreshDate', 'hasErrorLocationComment',
        'hasErrorGrade', 'hasErrorHireDate', 'hasErrorSalary', 'hasErrorJobClass', 'hasErrorJobAssign1',
        'hasErrorJobAssign2', 'hasErrorJobAssign3', 'hasErrorItinerantFlag',
      ];
      const error = errorFields.some(errorField => this[errorField]);

      if (error) {
        this.showNotification('danger', ['There were some errors. Please fix the errors highlighted in the fields below.']);
      } else {
        this.resetNotifications();
      }

      return error;
    },
    checkForFormChanges() {
      return this.compareObjects(this.personLocation, this.cachedInfo);
    },
    checkIfFieldIsEmptyAndRequired(field, code) {
      const value = this.personLocation[field];
      if (this.fieldRequired(code)) {
        return value != null ? value.length === 0 : true;
      }
      return false;
    },
    compareObjects(o, n) {
      return JSON.stringify(o) !== JSON.stringify(n);
    },
    editPersonLocation() {
      this.editCurrentPanel = true;
      this.hideNotification();
    },
    async fetchSchoolCodes() {
      const districtCode = Array.isArray(this.personLocation.districtCode)
        ? this.personLocation.districtCode[0].code
        : this.personLocation.districtCode;

      // Handle schoolCode as a string or array
      const schoolCode = Array.isArray(this.personLocation.schoolCode)
        ? this.personLocation.schoolCode[0].code
        : this.personLocation.schoolCode;
      const responses = await axios.get(`districts/${districtCode}/schools/${schoolCode}`);
      return [responses].map(r => r.data);
    },
    fieldLabel(code) {
      return this.getFieldLabelByPersonType(code, 'ASSIGN', this.cachedInfo.personType);
    },
    fieldLength(code) {
      return this.getFieldLengthsByPersonType(code, 'ASSIGN', this.cachedInfo.personType);
    },
    fieldEnabled(code) {
      return this.getFieldEnabledForPersonType(code, 'ASSIGN', this.cachedInfo.personType);
    },
    fieldRequired(code) {
      return this.getFieldRequiredForPersonType(code, 'ASSIGN', this.cachedInfo.personType);
    },
    formatNameAndCode(name, code) {
      return `${name} (${code})`.replace(/(\(\d+\))$/, '&nbsp;<i style="color: #737373; font-size: 0.75rem">$1</i>');
    },
    async getPersonLocations() {
      const { id } = this.$route.params;
      try {
        const url = this.isRetired
          ? `retired-person/${id}/location`
          : `person/${id}/location`;

        const { data } = await axios.get(url);
        return data;
      } catch (error) {
        console.error('Request failed:', error);
        throw error;
      }
    },
    async getResDistrictName() {
      if (!this.cachedInfo.resDistrictCode) {
        return this.resDistrictNameAndCode;
      }
      try {
        const response = await axios.get(`/districts/${this.cachedInfo.resDistrictCode}?allDistricts=true`);
        const districtName = response.data.location.name;
        const formattedCode = `&nbsp;<i style="color: #737373; font-size: 0.75rem">(${this.cachedInfo.resDistrictCode})</i>`;
        this.resDistrictNameAndCode = `${districtName} ${formattedCode}`;
      } catch (error) {
        console.error(error);
      }
      return this.resDistrictNameAndCode;
    },
    handlePaste() {
      // Optionally, let Vue update the model first
      this.$nextTick(() => {
        this.validateInputField('altPersonId');
        this.validateAltIDAndAltSourceLinkage();
      });
    },
    isFloatNumbers(evt) {
      const charCode = evt.which || evt.keyCode;
      const inputChar = String.fromCharCode(charCode);
      // Regular expression to allow digits (0-9) and optional decimal point (.)
      const pattern = /^[0-9.]*$/;
      if (!pattern.test(inputChar)) {
        evt.preventDefault(); // Prevent input if character does not match pattern
      }
    },
    makeCopy(obj) {
      if (obj === null || typeof obj !== 'object') return obj; // Handle primitive values
      const copy = Array.isArray(obj) ? [] : {}; // Determine whether it's an array or object

      // Use Object.keys() to iterate over only the object's own properties
      Object.keys(obj).forEach((key) => {
        copy[key] = this.makeCopy(obj[key]); // Recursively copy values
      });

      return copy;
    },
    mapDescription(collection, code) {
      const item = collection.find(i => i.code === code);
      return item ? item.description : '\u00A0';
    },
    mapGrades(gradeCode) {
      return this.mapDescription(this.activeGrades, gradeCode);
    },
    mapItinerantFlag(flag) {
      return this.mapDescription(this.activeItinerantFlags, flag);
    },
    mapJobClassCode(code) {
      return this.mapDescription(this.activeTitles, code);
    },
    mapJobAssignments(code) {
      return this.mapDescription(this.activeJobAssignments, code);
    },
    mapSource(code) {
      return this.mapDescription(this.sources, code);
    },
    nullifyDisabledFields() {
      const fields = [
        ['ACTIVE', 'active'],
        ['ALT_STUDENT_ID', 'altPersonId'],
        ['ALT_SOURCE_SIS_CODE', 'altSourceCode'],
        ['GRADE_CODE', 'gradeCode'],
        ['LOCAL_STUDENT_ID', 'localId'],
        ['LOCATION_COMMENT', 'locationComment'],
        ['RES_DISTRICT_CODE', 'resDistrictCode'],
        ['SOURCE_CODE', 'sourceCode'],
        ['SOURCE_REFRESH_DATE', 'sourceRefreshDate'],
      ];
      fields.forEach((e) => {
        if (!this.fieldEnabled(e[0])) {
          this.payLoad[e[1]] = null;
        }
      });
    },
    async onLoad() {
      this.loading = true;
      try {
        const [locationsResponse] = await Promise.all([
          this.getPersonLocations(),
        ]);

        const { id } = this.$route.params;
        const { districtCode, schoolCode, personType } = this.$route.query;

        this.personLocation = locationsResponse
          .find(location => location.personId === id
        && location.districtCode === districtCode
        && location.schoolCode === schoolCode
        && location.personType === personType) || {};

        if (Object.keys(this.personLocation).length === 0) {
          this.locationNotFound = true;
        } else {
          this.cachedInfo = this.makeCopy(this.personLocation);
          this.getResDistrictName();
        }
      } catch (error) {
        console.error(error);
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
    onPasteValidation(fieldName) {
      // Run validation after paste completes
      this.$nextTick(() => {
        this.validateInputField(fieldName);
      });
    },
    resetData() {
      this.personLocation = this.cachedInfo;
    },
    resetErrorAndMsg() {
      // Instead of calling this.initialData(), use this directly to access the current data.
      const errorKeys = [
        'SchoolYear', 'ResDistrict', 'School', 'Source', 'LocalId', 'AltSource',
        'AltPersonId', 'Active', 'SourceRefreshDate', 'LocationComment', 'Grade',
        'HireDate', 'Salary', 'JobClass', 'JobAssign1', 'JobAssign2', 'JobAssign3',
        'ItinerantFlag',
      ];

      // Clear the errors and messages dynamically from the errors object
      const data = {};

      errorKeys.forEach((key) => {
        data[`hasError${key}`] = false;
        data[`msg${key}`] = null;
      });

      // Now update the current data (this.$data)
      Object.assign(this.$data, data);
    },
    setActiveStatusOptions() {
      this.activeStatusOptions = [
        { code: false, description: 'Inactive' },
        { code: true, description: 'Active' },
      ];
    },
    async updateLocation() {
      if (this.checkFieldValidations() || this.validateAltIDAndAltSourceLinkage()) {
        this.showNotification('danger', ['There were some errors.Please fix the errors highlighted in the fields below.']);
        return;
      }
       const changes = this.checkForFormChanges();
      if (!changes) {
        this.editCurrentPanel = false;
        this.hideNotification();
        return;
      }
      try {
        this.hideNotification();
        this.loading = true;
        this.payLoad = this.tempPayLoad;
        this.nullifyDisabledFields();
        await axios.put(`/person/${this.cachedInfo.personId}/location/${this.cachedInfo.districtCode}/${this.cachedInfo.schoolCode}/${this.cachedInfo.personType}`, this.payLoad);
        this.editCurrentPanel = false;
        this.$router.replace({
          query: {
            ...this.$route.query, // Preserve existing query parameters
            schoolCode: this.payLoad.schoolCode, // Update schoolCode only if Conc Loc Assignm is NO
          },
        });
        this.$root.$emit('reloadPerson');
        this.showNotification('success', ['The location information has been updated successfully.']);
        this.cachedInfo = this.makeCopy(this.personLocation);
      } catch (error) {
        this.showNotification('danger', [error.response.data.message]);
        console.error(error);
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
    resetNotifications() {
      this.hideNotification();
      this.$store.state.notifications.isActive = false;
      this.$store.state.notifications.notificationType = null;
    },
    validateSalary() {
      const { salary } = this.personLocation;
      if (this.fieldRequired('SALARY') && !salary) {
        this.hasErrorSalary = true;
        this.msgSalary = `${this.fieldLabel('SALARY')} is required.`;
      } else if (salary && salary.toString().replace('.', '').length > this.fieldLength('SALARY')) {
        this.hasErrorSalary = true;
        this.msgSalary = `${this.fieldLabel('SALARY')} exceeds character limit 10.2`;
      } else {
        this.hasErrorSalary = false;
        this.msgSalary = null;
      }
    },
    validateDateField(field) {
      const Obj = {
        sourceRefreshDate: ['SOURCE_REFRESH_DATE', 'hasErrorSourceRefreshDate', 'msgSourceRefreshDate'],
        hireDate: ['HIRE_DATE', 'hasErrorHireDate', 'msgHireDate'],
      };
      if (Object.keys(Obj).includes(field)) {
        const [code, hasError, msg] = Obj[field];
        if (this.fieldRequired(code) && !this.personLocation[field]) {
          this[hasError] = true;
          this[msg] = `${this.fieldLabel(code)} is required.`;
        } else if (this.personLocation[field]
          && !this.isValidDateFormat(this.toDateInputValue(this.personLocation[field]), 'YYYY-MM-DD')) {
          this[hasError] = true;
          this[msg] = `${this.fieldLabel(code)} is invalid.`;
        } else {
          this[hasError] = false;
          this[msg] = null;
        }
      }
    },
    validateInputField(field) {
      const makeField = (code, hasErrorKey, msgKey) => [
        code, hasErrorKey, msgKey, this.fieldLength(code),
      ];

      const Obj = {
        localId: makeField('LOCAL_STUDENT_ID', 'hasErrorLocalId', 'msgLocalId'),
        altPersonId: makeField('ALT_STUDENT_ID', 'hasErrorAltPersonId', 'msgAltPersonId'),
        locationComment: makeField('LOCATION_COMMENT', 'hasErrorLocationComment', 'msgLocationComment'),
      };

      if (Object.keys(Obj).includes(field)) {
        const [code, hasError, msg, maxLength] = Obj[field];
        if (this.fieldRequired(code) && !this.personLocation[field]) {
          this[hasError] = true;
          this[msg] = `${this.fieldLabel(code)} is required.`;
        } else if (this.personLocation[field] && this.personLocation[field].length > maxLength) {
          this[hasError] = true;
          this[msg] = `${this.fieldLabel(code)} allows a maximum of ${maxLength} characters.`;
        } else {
          this[hasError] = false;
          this[msg] = null;
        }
      }
    },
    validateSelectField(field) {
      const ObjEsSelect = {
        gradeCode: ['GRADE_CODE', 'hasErrorGrade', 'msgGrade'],
        schoolCode: ['SCHOOL_CODE', 'hasErrorSchool', 'msgSchool'],
        sourceCode: ['SOURCE_CODE', 'hasErrorSource', 'msgSource'],
        schoolYear: ['SCHOOL_YEAR', 'hasErrorSchoolYear', 'msgSchoolYear'],
        resDistrictCode: ['RES_DISTRICT_CODE', 'hasErrorResDistrict', 'msgResDistrict'],
        altSourceCode: ['ALT_SOURCE_SIS_CODE', 'hasErrorAltSource', 'msgAltSource'],
        active: ['ACTIVE', 'hasErrorActive', 'msgActive'],
        jobClassCode: ['JOB_CLASS_CODE', 'hasErrorJobClass', 'msgJobClass'],
        jobAssignment1: ['JOB_ASSIGNMENT_1', 'hasErrorJobAssign1', 'msgJobAssign1'],
        jobAssignment2: ['JOB_ASSIGNMENT_2', 'hasErrorJobAssign2', 'msgJobAssign2'],
        jobAssignment3: ['JOB_ASSIGNMENT_3', 'hasErrorJobAssign3', 'msgJobAssign3'],
        itinerantFlag: ['ITINERANT_FLAG', 'hasErrorItinerantFlag', 'msgItinerantFlag'],
      };

      if (Object.keys(ObjEsSelect).includes(field)) {
        const [code, hasError, msg] = ObjEsSelect[field];
        const isFieldRequired = this.checkIfFieldIsEmptyAndRequired(field, code);
        if (isFieldRequired) {
          this[hasError] = true;
          this[msg] = `${this.fieldLabel(code)} is required.`;
        } else {
          this[hasError] = false;
          this[msg] = null;

          // Special case for school year
          if (field === 'schoolYear') {
            if (!this.schoolYears.find(a => a.year === this.tempPayLoad.schoolYear)) {
              this[hasError] = true;
              this[msg] = `${this.fieldLabel(code)} is invalid.`;
            }
          }
        }
      }
    },
    validateAltIDAndAltSourceLinkage() {
      if (!('altSourceCode' in this.personLocation)) {
        this.personLocation.altSourceCode = [];
      }

      if (!('altPersonId' in this.personLocation)) {
        this.personLocation.altPersonId = null;
      }

      const { altPersonId, altSourceCode } = this.personLocation;

      const isAltPersonIdEmpty = !altPersonId || altPersonId.trim() === '';
      const isAltSourceCodeEmpty = !altSourceCode || altSourceCode.length === 0;

      const maxLength = this.fieldLength('ALT_STUDENT_ID');
      const exceedsMaxLength = altPersonId && altPersonId.length > maxLength;

      // ALT STUDENT ID validation
      if (isAltPersonIdEmpty && !isAltSourceCodeEmpty) {
        // Only override if max length error isn't already present
        if (!exceedsMaxLength) {
          this.hasErrorAltPersonId = true;
          this.msgAltPersonId = `${this.fieldLabel('ALT_STUDENT_ID')} is required.`;
        }
      } else if (!exceedsMaxLength) {
        // Clear error only if max length is not violated
        this.hasErrorAltPersonId = false;
        this.msgAltPersonId = null;
      }

      // ALT SOURCE CODE validation
      if (!isAltPersonIdEmpty && isAltSourceCodeEmpty) {
        this.hasErrorAltSource = true;
        this.msgAltSource = `${this.fieldLabel('ALT_SOURCE_SIS_CODE')} is required.`;
      } else {
        this.hasErrorAltSource = false;
        this.msgAltSource = null;
      }

      return this.hasErrorAltPersonId || this.hasErrorAltSource;
    },
  },
};
</script>

<style lang="sass" scoped>
.border-red
  border-color: red
.control
  padding-left: 30px
.columns
  margin: 0
  display: flex
  flex-wrap: wrap
.column
  padding: 0
  flex: 1 1 auto
.control
  padding-left: 0 !important
.disabled-textarea
  border: 0
  resize: none
  overflow: auto
  padding-left: 0
  padding-right: 0
  cursor: default
.district-code
  font-size: 14px
  color: #6C757D
  font-style: italic
  font-weight: 400
.district-name
  color: #212529
  font-size: 18.4px
  font-weight: 700
  text-align: left
  padding-left: 2rem
.icon-right
  padding-right: 1.5rem
  padding-top: 1.5rem
.is-static
  pointer-events: none
  height: 100%
  text-overflow: ellipsis
.label
  width: 200px
.margin--top
  margin-top: 1rem
.school-code
  font-size: 12px
  color: #6C757D
  font-weight: 400
.school-name
  color: #212529
  font-size: 14px
  font-weight: 700
.textarea-wrapper
  position: relative
  overflow: auto
.textarea-wrapper textarea:focus
  outline: none
.row .field
  display: flex
  flex-wrap: wrap
  min-width: 100%
  box-sizing: border-box
  align-items: center
  .field-label:not(.control)
    flex: 1
  .field-body
    flex: 2
    .field
      display: block
.es-form-section
  overflow-x: auto
.field-label.is-normal
  padding-top: 0
.field-body .field .control.field-label /deep/ .control
  width: 100%
.main-column
  flex: 1 1 50%
.help
  margin-bottom: 0.25rem;
.input
  text-overflow: ellipsis
@media screen and (max-width: 70em)
  .main-column
    flex: 1 1 100%
</style>
